import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  RotateCw,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Info,
  Sparkles,
  TrendingUp,
  Clock,
  Calendar,
  Wallet,
  Repeat,
  Activity,
  CreditCard,
  ShoppingBag,
  CheckCircle2,
  ArrowUpRight,
} from 'lucide-react';
import { customerService } from '../../services/customer.service';
import { formatCustomDate } from '../../helpers/formatCustomDate';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';

// Helper: map Score_Rank to thematic styles, icons, and descriptions
const getRankDetails = (rank = '') => {
  const normalized = rank.toLowerCase().trim();
  if (normalized.includes('poor')) {
    return {
      label: 'Poor',
      colorText: 'text-rose-700',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      badgeClass: 'bg-rose-100 text-rose-800 border-rose-300',
      indicatorBg: 'bg-rose-500',
      gradient: 'from-rose-500 to-red-600',
      icon: ShieldAlert,
      description: 'High credit risk based on recent activity, balance, or order throughput.',
    };
  }
  if (normalized.includes('fair')) {
    return {
      label: 'Fair',
      colorText: 'text-amber-700',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      badgeClass: 'bg-amber-100 text-amber-800 border-amber-300',
      indicatorBg: 'bg-amber-500',
      gradient: 'from-amber-500 to-orange-500',
      icon: AlertTriangle,
      description: 'Moderate credit profile. Targeted purchasing and wallet consistency will unlock higher limits.',
    };
  }
  if (normalized.includes('very good')) {
    return {
      label: 'Very Good',
      colorText: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      indicatorBg: 'bg-emerald-500',
      gradient: 'from-emerald-500 to-teal-600',
      icon: ShieldCheck,
      description: 'Strong credit profile with dependable order history and high repayment confidence.',
    };
  }
  if (normalized.includes('good')) {
    return {
      label: 'Good',
      colorText: 'text-sky-700',
      bgColor: 'bg-sky-50',
      borderColor: 'border-sky-200',
      badgeClass: 'bg-sky-100 text-sky-800 border-sky-300',
      indicatorBg: 'bg-sky-500',
      gradient: 'from-sky-500 to-blue-600',
      icon: ShieldCheck,
      description: 'Healthy profile with regular activity and dependable payment cadence.',
    };
  }
  if (normalized.includes('excellent') || normalized.includes('exceptional')) {
    return {
      label: normalized.includes('exceptional') ? 'Exceptional' : 'Excellent',
      tier: 'Excellent',
      colorText: 'text-indigo-700',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      badgeClass: 'bg-indigo-100 text-indigo-800 border-indigo-300',
      indicatorBg: 'bg-indigo-500',
      gradient: 'from-indigo-500 to-purple-600',
      icon: Sparkles,
      description: 'Exceptional credit rating with access to priority food basket financing and highest limits.',
    };
  }
  return {
    label: rank || 'Not Rated',
    colorText: 'text-slate-700',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    badgeClass: 'bg-slate-100 text-slate-800 border-slate-300',
    indicatorBg: 'bg-slate-500',
    gradient: 'from-slate-500 to-gray-600',
    icon: Info,
    description: 'Score evaluation based on historical interaction and balance metrics.',
  };
};

// Helper: rating for sub-factors (0 - 100)
const getFactorRating = (val = 0) => {
  const num = Number(val) || 0;
  if (num >= 75) {
    return {
      label: 'Optimal',
      badgeClass: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      barColor: 'bg-emerald-500',
      textColor: 'text-emerald-700',
      iconBg: 'bg-emerald-50 text-emerald-600',
    };
  }
  if (num >= 50) {
    return {
      label: 'Moderate',
      badgeClass: 'bg-sky-50 text-sky-700 border border-sky-200',
      barColor: 'bg-sky-500',
      textColor: 'text-sky-700',
      iconBg: 'bg-sky-50 text-sky-600',
    };
  }
  if (num >= 25) {
    return {
      label: 'Fair',
      badgeClass: 'bg-amber-50 text-amber-700 border border-amber-200',
      barColor: 'bg-amber-500',
      textColor: 'text-amber-700',
      iconBg: 'bg-amber-50 text-amber-600',
    };
  }
  return {
    label: 'Needs Focus',
    badgeClass: 'bg-rose-50 text-rose-700 border border-rose-200',
    barColor: 'bg-rose-500',
    textColor: 'text-rose-700',
    iconBg: 'bg-rose-50 text-rose-600',
  };
};

// Accepts both API casings (e.g. score_rank and Score_Rank) by adding the Pascal_Case keys
const normalizeCreditScore = record => {
  if (!record) return null;
  const pascal = Object.fromEntries(
    Object.entries(record).map(([key, value]) => [
      key
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join('_'),
      value,
    ]),
  );
  return { ...pascal, ...record };
};

// Standard score tier thresholds with compact mobile labels
const TIERS = [
  { label: 'Poor', shortLabel: 'Poor', range: '300-579', color: 'bg-rose-500' },
  { label: 'Fair', shortLabel: 'Fair', range: '580-669', color: 'bg-amber-500' },
  { label: 'Good', shortLabel: 'Good', range: '670-739', color: 'bg-sky-500' },
  { label: 'Very Good', shortLabel: 'V. Good', range: '740-799', color: 'bg-emerald-500' },
  { label: 'Excellent', shortLabel: 'Excel.', range: '800-850', color: 'bg-indigo-500' },
];

function CustomerCreditScore(props) {
  // When `data` is passed the parent owns fetching; `onReload` and `loading` come with it
  const { customer_id, Zoho_Customer_Id, customerData, data, onReload } = props;
  const isControlled = data !== undefined;
  const [fetchedScore, setCreditScore] = useState(null);
  const [fetching, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const creditScore = useMemo(() => normalizeCreditScore(isControlled ? data : fetchedScore), [isControlled, data, fetchedScore]);
  const loading = isControlled ? !!props.loading : fetching;

  const targetCustomerId = customer_id || Zoho_Customer_Id || props?.id;
  const currencySymbol = customerData?.Currency_Symbol || '₦';

  const fetchCreditScore = useCallback(() => {
    if (isControlled || !targetCustomerId) return;
    setLoading(true);
    setError(null);

    customerService
      .customerCreditScore(targetCustomerId)
      .then(res => {
        if (Array.isArray(res?.data) && res.data.length > 0) {
          setCreditScore(res.data[0]);
        } else if (res?.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
          setCreditScore(res.data);
        } else {
          setCreditScore(null);
        }
      })
      .catch(err => {
        console.error('Credit score fetch error:', err);
        setError(err);
        setCreditScore(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isControlled, targetCustomerId]);

  const reload = isControlled ? onReload : fetchCreditScore;

  useEffect(() => {
    fetchCreditScore();
  }, [fetchCreditScore]);

  // Derived Values
  const currentScore = Number(creditScore?.Final_Score ?? creditScore?.Score ?? 0);
  const baseScore = Number(creditScore?.Base_Score ?? 300);
  const scoreRank = creditScore?.Score_Rank ?? 'Poor';
  const rankInfo = useMemo(() => getRankDetails(scoreRank), [scoreRank]);

  // Percentage along the standard 300 - 850 range
  const scorePercent = useMemo(() => {
    if (!currentScore) return 0;
    const clamped = Math.min(850, Math.max(300, currentScore));
    return Math.round(((clamped - 300) / (850 - 300)) * 100);
  }, [currentScore]);

  // Parse semicolon-separated recommendations
  const recommendationList = useMemo(() => {
    const raw = creditScore?.Recommendations;
    if (!raw || typeof raw !== 'string') return [];
    return raw
      .split(';')
      .map(item => item.trim())
      .filter(Boolean);
  }, [creditScore?.Recommendations]);

  // 6 Factor breakdown pillars
  const factors = useMemo(() => {
    return [
      {
        id: 'recency',
        title: 'Interaction Recency',
        score: Number(creditScore?.Recency_Recommendation ?? 0),
        icon: Clock,
        description: 'Time elapsed since latest customer order or interaction.',
        metricHint: 'Recent activity maintains high model confidence.',
      },
      {
        id: 'subscription',
        title: 'Subscription Standing',
        score: Number(creditScore?.Subscription_Recommendation ?? 0),
        icon: Repeat,
        description: 'Recurring order commitments and active recurring plan status.',
        metricHint: 'Live subscription plans unlock consistent credit weight.',
      },
      {
        id: 'age',
        title: 'Account Longevity',
        score: Number(creditScore?.Age_Recommendation ?? creditScore?.Age_Recom ?? 0),
        icon: Calendar,
        description: 'Historical duration of customer relationship on platform.',
        metricHint: 'Accounts with sustained tenure gain higher baseline trust.',
      },
      {
        id: 'balance',
        title: 'Wallet & Balance Health',
        score: Number(creditScore?.Balance_Recommendation ?? 0),
        icon: Wallet,
        description: 'Average wallet funding balance and settlement reliability.',
        metricHint: 'Maintaining funded wallet buffers improves liquidity rank.',
      },
      {
        id: 'frequency',
        title: 'Order Cadence',
        score: Number(creditScore?.Frequency_Recommendation ?? creditScore?.Freq_Recom ?? 0),
        icon: Activity,
        description: 'Consistency and frequency of repeat orders over rolling months.',
        metricHint: 'Steady ordering rhythm prevents score decay.',
      },
      {
        id: 'transaction',
        title: 'Transaction Volume',
        score: Number(creditScore?.Transaction_Recommendation ?? 0),
        icon: CreditCard,
        description: 'Cumulative volume and completed order throughput value.',
        metricHint: 'Higher fulfilled volume deepens transaction profile.',
      },
    ];
  }, [creditScore]);

  const RankIcon = rankInfo.icon;

  // Render: Loading skeleton
  if (loading && !creditScore) {
    return (
      <div className="w-full max-w-full space-y-4 p-2 sm:space-y-6 sm:p-4">
        <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="h-6 w-44 animate-pulse rounded bg-slate-200 sm:w-48" />
          <div className="h-9 w-full animate-pulse rounded-xl bg-slate-200 sm:w-28" />
        </div>
        <div className="h-56 w-full animate-pulse rounded-2xl bg-slate-100 sm:h-64" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
          <div className="h-24 animate-pulse rounded-2xl bg-slate-100" />
          <div className="h-24 animate-pulse rounded-2xl bg-slate-100" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-36 animate-pulse rounded-2xl bg-slate-100" />
          ))}
        </div>
      </div>
    );
  }

  // Render: Empty state
  if (!loading && !creditScore && !error) {
    return (
      <div className="flex w-full max-w-full flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center shadow-sm sm:p-12">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400 sm:h-16 sm:w-16">
          <ShieldAlert className="h-7 w-7 sm:h-8 sm:w-8" />
        </div>
        <h3 className="mt-4 text-base font-semibold text-slate-800 sm:text-lg">No Credit Score Found</h3>
        <p className="mt-1 max-w-md text-xs text-slate-500 sm:text-sm">
          There is no calculated credit score record available for customer #{targetCustomerId || 'N/A'}. Click reload to query the latest
          computation.
        </p>
        <button
          type="button"
          onClick={reload}
          disabled={loading}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 sm:w-auto"
        >
          <RotateCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Reload / Check Score</span>
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full space-y-4 overflow-hidden pb-8 sm:space-y-6">
      {/* Top Header & Actions */}
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">Financial Credit Score</h2>
            <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
              ID: #{targetCustomerId || 'N/A'}
            </span>
          </div>
          <p className="mt-1 break-words text-[11px] text-slate-500 sm:text-xs">
            Last Processed:{' '}
            <span className="font-medium text-slate-700">
              {creditScore?.Processed_Date ? formatCustomDate(creditScore.Processed_Date) : 'Recently evaluated'}
            </span>
            {creditScore?.Updated_Date && creditScore.Updated_Date !== creditScore.Processed_Date && (
              <>
                <span className="hidden sm:inline"> • </span>
                <span className="mt-0.5 block sm:hidden">Updated: </span>
                <span className="font-medium text-slate-700">{formatCustomDate(creditScore.Updated_Date)}</span>
              </>
            )}
          </p>
        </div>

        <button
          type="button"
          onClick={reload}
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:text-slate-900 active:scale-95 disabled:opacity-50 sm:w-auto"
          title="Refresh credit score data"
        >
          <RotateCw className={`h-4 w-4 text-slate-500 ${loading ? 'animate-spin text-primary' : ''}`} />
          <span>{loading ? 'Refreshing...' : 'Reload Score'}</span>
        </button>
      </div>

      {/* Hero Card: Main Score & Gauge Bar */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-6">
        {/* Subtle decorative gradient glow behind score */}
        <div className={`pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full opacity-10 blur-3xl ${rankInfo.bgColor}`} />

        <div className="flex flex-col gap-5 sm:gap-6 lg:flex-row lg:items-center lg:justify-between">
          {/* Left: Numerical Score & Rank Badge */}
          <div className="flex flex-col justify-center space-y-2.5 sm:space-y-3">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 sm:text-xs">Current Credit Score</span>
              <span className="text-[11px] text-slate-400 sm:text-xs">• Base: {baseScore}</span>
            </div>

            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
              <div className="flex items-baseline gap-1.5">
                <span className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
                  {currentScore || 'N/A'}
                </span>
                <span className="text-base font-semibold text-slate-400 sm:text-lg">/ 850</span>
              </div>

              {/* Rank Pill Badge */}
              <div
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold shadow-sm sm:text-sm ${rankInfo.badgeClass}`}
              >
                <span className={`h-2 w-2 rounded-full ${rankInfo.indicatorBg} animate-pulse`} />
                <RankIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{rankInfo.label}</span>
              </div>
            </div>

            <p className="max-w-xl text-xs leading-relaxed text-slate-600 sm:text-sm">{rankInfo.description}</p>
          </div>

          {/* Right: Quick Highlights (Eligibility & Scale) */}
          <div className="flex w-full flex-col gap-2.5 sm:flex-row sm:items-center lg:w-auto lg:flex-col lg:items-end">
            {creditScore?.Eligible_Food_Baskets ? (
              <div className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/80 px-3.5 py-2 text-xs font-semibold text-emerald-800 shadow-sm sm:w-auto sm:justify-start">
                <ShoppingBag className="h-4 w-4 shrink-0 text-emerald-600" />
                <span className="truncate">Eligible Baskets: {creditScore.Eligible_Food_Baskets}</span>
              </div>
            ) : (
              <div className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-xs font-medium text-slate-600 sm:w-auto sm:justify-start">
                <ShoppingBag className="h-4 w-4 shrink-0 text-slate-400" />
                <span>No Food Baskets Unlocked Yet</span>
              </div>
            )}

            <div className="inline-flex items-center justify-center gap-1.5 text-[11px] text-slate-500 sm:justify-start sm:text-xs">
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-amber-500" />
              <span>Score Scale: 300 (Min) – 850 (Max)</span>
            </div>
          </div>
        </div>

        {/* Visual Tiered Meter / Progress Track */}
        <div className="mt-6 space-y-2 border-t border-slate-100 pt-5 sm:mt-8 sm:pt-6">
          <div className="flex items-center justify-between text-[11px] font-medium text-slate-500 sm:text-xs">
            <span>Score Position: {scorePercent}% of range</span>
            <span className="font-semibold text-slate-700">Tier: {rankInfo.label}</span>
          </div>

          {/* Multi-segment colored track */}
          <div className="relative h-3 w-full overflow-hidden rounded-full bg-slate-100 p-0.5 shadow-inner sm:h-3.5">
            <div className="grid h-full w-full grid-cols-5 gap-0.5 overflow-hidden rounded-full sm:gap-1">
              {TIERS.map(tier => (
                <div key={tier.label} className={`h-full opacity-30 ${tier.color}`} title={tier.label} />
              ))}
            </div>

            {/* Filled highlight bar */}
            <div
              className={`absolute left-0.5 top-0.5 h-2 rounded-full bg-gradient-to-r shadow-sm transition-all duration-700 sm:h-2.5 ${rankInfo.gradient}`}
              style={{ width: `${Math.max(4, scorePercent)}%` }}
            />
          </div>

          {/* Tier labels underneath track */}
          <div className="grid grid-cols-5 gap-0.5 pt-1 text-center text-[9px] font-semibold uppercase tracking-wider text-slate-400 sm:gap-1 sm:text-[11px]">
            {TIERS.map(tier => {
              const isActive = tier.label.toLowerCase() === (rankInfo.tier ?? rankInfo.label).toLowerCase();
              return (
                <div key={tier.label} className={`transition-colors ${isActive ? 'font-bold text-slate-900' : 'text-slate-400'}`}>
                  <span className="sm:hidden">{tier.shortLabel}</span>
                  <span className="hidden sm:inline">{tier.label}</span>
                  <div className="hidden font-normal text-slate-400 md:block">({tier.range})</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Financial & Activity Summary Metrics */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
        <div className="flex items-center gap-3.5 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:gap-4 sm:p-5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600 sm:h-12 sm:w-12">
            <Wallet className="h-5 w-5 sm:h-6 sm:w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-slate-400 sm:text-xs">
              Average Monthly Spend
            </div>
            <div className="mt-0.5 truncate text-xl font-extrabold text-slate-900 sm:text-2xl">
              {currencySymbol}
              {formatCustomNumber(creditScore?.Average_Monthly_Spend ?? 0)}
            </div>
            <div className="truncate text-[11px] text-slate-500 sm:text-xs">Calculated over historical periods</div>
          </div>
        </div>

        <div className="flex items-center gap-3.5 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:gap-4 sm:p-5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 sm:h-12 sm:w-12">
            <Activity className="h-5 w-5 sm:h-6 sm:w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-slate-400 sm:text-xs">Purchase Frequency</div>
            <div className="mt-0.5 truncate text-xl font-extrabold text-slate-900 sm:text-2xl">
              {creditScore?.Purchase_Frequency ?? 0} <span className="text-xs font-semibold text-slate-500 sm:text-sm">orders / mo</span>
            </div>
            <div className="truncate text-[11px] text-slate-500 sm:text-xs">Regular order placement cadence</div>
          </div>
        </div>
      </div>

      {/* 6 Score Factor Breakdown Pillars */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900 sm:text-base">Score Breakdown Factors</h3>
          <p className="text-[11px] text-slate-500 sm:text-xs">Granular scoring components that determine this customer's credit score.</p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3">
          {factors.map(factor => {
            const FactorIcon = factor.icon;
            const rating = getFactorRating(factor.score);

            return (
              <div
                key={factor.id}
                className="flex flex-col justify-between rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition-all hover:border-slate-300 hover:shadow-md sm:p-5"
              >
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl sm:h-10 sm:w-10 ${rating.iconBg}`}>
                        <FactorIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="truncate text-xs font-bold text-slate-900 sm:text-sm">{factor.title}</h4>
                        <span className={`inline-block rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${rating.badgeClass}`}>
                          {rating.label}
                        </span>
                      </div>
                    </div>

                    <div className="shrink-0 text-right">
                      <span className="text-lg font-extrabold text-slate-900 sm:text-xl">{factor.score}</span>
                      <span className="text-[10px] text-slate-400 sm:text-xs">/100</span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-3.5 h-2 w-full overflow-hidden rounded-full bg-slate-100 sm:mt-4">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${rating.barColor}`}
                      style={{ width: `${Math.min(100, Math.max(4, factor.score))}%` }}
                    />
                  </div>

                  <p className="mt-2.5 text-[11px] leading-relaxed text-slate-500 sm:mt-3 sm:text-xs">{factor.description}</p>
                </div>

                <div className="mt-3 border-t border-slate-100 pt-2 text-[10px] text-slate-400 sm:text-[11px]">
                  <span className="font-medium text-slate-600">Tip:</span> {factor.metricHint}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actionable Recommendations Section */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <TrendingUp className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 sm:text-base">Score Improvement Recommendations</h3>
            <p className="text-[11px] text-slate-500 sm:text-xs">
              Personalized action steps generated to elevate customer rating and unlock higher limits.
            </p>
          </div>
        </div>

        {recommendationList.length > 0 ? (
          <div className="mt-4 grid grid-cols-1 gap-2.5 sm:mt-5 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3">
            {recommendationList.map((rec, index) => (
              <div
                key={index}
                className="group flex flex-col justify-between rounded-xl border border-slate-100 bg-slate-50/70 p-3.5 transition-all hover:border-slate-200 hover:bg-white hover:shadow-sm sm:p-4"
              >
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary sm:h-6 sm:w-6">
                    <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </div>
                  <p className="text-[11px] font-medium leading-relaxed text-slate-800 sm:text-xs">{rec}</p>
                </div>

                <div className="mt-2.5 flex items-center justify-between text-[10px] font-semibold text-primary sm:mt-3 sm:text-[11px]">
                  <span className="inline-flex items-center gap-1">
                    <ArrowUpRight className="h-3 w-3" />
                    <span>Actionable step</span>
                  </span>
                  <span className="text-[10px] text-slate-400">Step {index + 1}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-slate-50 p-3.5 text-xs text-slate-600">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
            <span>Customer profile is in optimal standing with no pending improvement actions.</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerCreditScore;
